import { useState, useEffect, useMemo } from 'react';
import { TablePagination } from '@mui/material';
import moment from 'moment/moment';
import { getRequest } from '../../../../config';

const FEATURE_LABELS = {
    'filing-assistant-allocation': 'Allocation – Filing Assistant',
    'filing-assistant-autofill': 'Auto-fill – Filing Assistant',
    'filing-assistant-other-modules': 'Other Modules – Filing Assistant',
};

const PROVIDER_META = {
    claude: { label: 'Claude', color: '#4A67FF', bg: '#EEF1FF' },
    gpt: { label: 'GPT', color: '#0BA26F', bg: '#E7F8F1' },
    embedding: { label: 'Embeddings', color: '#F0AE0D', bg: '#FDF5E3' },
};

const formatCost = (v) => (v == null ? '—' : v > 0 && v < 0.01 ? '<$0.01' : `$${Number(v).toFixed(2)}`);
const formatTokens = (v) => (v || 0).toLocaleString();

// One run's summary row + an expandable breakdown of that run's cost by provider (Claude / GPT / Embeddings) —
// each run is already one AiUsageLog document with provider sub-totals pre-aggregated server-side.
const RunRow = ({ run }) => {
    const [open, setOpen] = useState(false);
    const providerRows = ['claude', 'gpt', 'embedding']
        .map((key) => ({ key, ...PROVIDER_META[key], ...run[key] }))
        .filter((p) => p.calls > 0);

    return (
        <div className="bg1 br10px bw1px bss bo29 mb1rem oh">
            <div
                onClick={() => setOpen(o => !o)}
                className="df aic px1-5rem py1-5rem cp bg5-hover td250"
                style={{ gap: '1.5rem' }}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ transition: 'transform 200ms', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', flexShrink: 0 }}>
                    <path d="M9 6l6 6-6 6" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                <div className="df fdc" style={{ flex: '1 1 160px', minWidth: 0 }}>
                    <span className="fs0-875rem fw500 c15 oh" style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{run.userId ? `${run.userId.firstName || ''} ${run.userId.lastName || ''}`.trim() || '—' : '—'}</span>
                    <span className="fs0-75rem fw400 c28">{moment(run.createdAt).format('MMM D, YYYY h:mm A')}</span>
                </div>

                <div className="df fdc" style={{ flex: '1 1 180px', minWidth: 0 }}>
                    <span className="fs0-875rem fw600 c6 oh" style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{run.companyId?.cName || '—'}</span>
                </div>

                <div className="df fdc" style={{ flex: '1 1 180px', minWidth: 0 }}>
                    <span className="fs0-8rem fw500 c14 oh" style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{FEATURE_LABELS[run.feature] || run.feature}</span>
                </div>

                <div className="df aic" style={{ flex: '1 1 220px', gap: '0.4rem' }}>
                    {providerRows.map(p => (
                        <span key={p.key} className="fs0-75rem fw600 br50px px0-75rem py0-25rem" style={{ background: p.bg, color: p.color }}>
                            {p.label}
                        </span>
                    ))}
                </div>

                <div className="df fdc tar" style={{ flex: '0 0 100px' }}>
                    <span className="fs0-875rem fw500 c15">{run.totalCalls}</span>
                    <span className="fs0-75rem fw400 c28">calls</span>
                </div>

                <div className="df fdc tar" style={{ flex: '0 0 110px' }}>
                    <span className="fs0-875rem fw500 c15">{formatTokens(run.totalTokens)}</span>
                    <span className="fs0-75rem fw400 c28">tokens</span>
                </div>

                <div className="df fdc tar" style={{ flex: '0 0 90px' }}>
                    <span className="fs1rem fw600 c40">{formatCost(run.totalCost)}</span>
                </div>
            </div>

            {open && (
                <div className="bg54 px1-5rem py1rem" style={{ borderTop: '1px solid #E8EBF5' }}>
                    <div className="df fdc" style={{ gap: '0.5rem' }}>
                        {providerRows.map((p) => (
                            <div key={p.key} className="df aic bg1 br10px px1rem py0-75rem bw1px bss bo29" style={{ gap: '0.75rem' }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                                <span className="fs0-8rem fw600 c15" style={{ flex: '0 0 110px' }}>{p.label}</span>
                                <span className="fs0-75rem fw400 c28" style={{ flex: 1 }}>{p.calls} call{p.calls !== 1 ? 's' : ''}</span>
                                <span className="fs0-8rem fw500 c14" style={{ flex: '0 0 100px', textAlign: 'right' }}>{formatTokens(p.tokens)} tok</span>
                                <span className="fs0-8rem fw600 c6" style={{ flex: '0 0 80px', textAlign: 'right' }}>{formatCost(p.cost)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const StatTile = ({ label, value, color }) => (
    <div className="bg38 br10px p1-5rem" style={{ flex: 1 }}>
        <p className="fs1-75rem fw600 mb0-25rem" style={{ color }}>{value}</p>
        <p className="fs0-8rem fw400 c28 mb0rem">{label}</p>
    </div>
);

const AiCosting = () => {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(50);
    const [feature, setFeature] = useState('');
    const [provider, setProvider] = useState('');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: page + 1, limit });
            if (feature) params.append('feature', feature);
            if (provider) params.append('provider', provider);
            if (search.trim()) params.append('search', search.trim());
            const response = await getRequest(`/admin/ai-usage-logs?${params.toString()}`);
            if (response?.code === 200) {
                setLogs(response.body.logs || []);
                setTotal(response.body.total || 0);
            }
        } catch (error) {
            console.error('Error fetching AI usage logs:', error);
        } finally {
            setLoading(false);
        }
    };

    // Debounce the free-text search so we don't fire a request on every keystroke
    useEffect(() => {
        const timer = setTimeout(() => { setPage(0); fetchLogs(); }, 350);
        return () => clearTimeout(timer);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchLogs(); }, [page, limit, feature, provider]); // eslint-disable-line react-hooks/exhaustive-deps

    const pageStats = useMemo(() => ({
        cost: logs.reduce((sum, r) => sum + (r.totalCost || 0), 0),
        tokens: logs.reduce((sum, r) => sum + (r.totalTokens || 0), 0),
        calls: logs.reduce((sum, r) => sum + (r.totalCalls || 0), 0),
    }), [logs]);

    return (
        <div>
            <div className="df aic mb1-5rem" style={{ gap: '1rem' }}>
                <StatTile label="Total runs (this page)" value={total} color="#151313" />
                <StatTile label="Tokens used (this page)" value={formatTokens(pageStats.tokens)} color="#4A67FF" />
                <StatTile label="AI actions performed (this page)" value={pageStats.calls} color="#0BA26F" />
                <StatTile label="Total cost (this page)" value={formatCost(pageStats.cost)} color="#40C353" />
            </div>

            <div className="df aic jcsb mb1-5rem" style={{ gap: '0.75rem' }}>
                <div className="df aic" style={{ gap: '0.75rem' }}>
                    <select
                        value={feature}
                        onChange={(e) => { setPage(0); setFeature(e.target.value); }}
                        className="br50px px1-5rem py0-5rem bw1px bss bo29 bg1 fs0-875rem fw500 c15 cp"
                        style={{ minWidth: 260, appearance: 'none', outline: 'none' }}
                    >
                        <option value="">All Features</option>
                        {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>

                    <select
                        value={provider}
                        onChange={(e) => { setPage(0); setProvider(e.target.value); }}
                        className="br50px px1-5rem py0-5rem bw1px bss bo29 bg1 fs0-875rem fw500 c15 cp"
                        style={{ minWidth: 180, appearance: 'none', outline: 'none' }}
                    >
                        <option value="">All Providers</option>
                        {Object.entries(PROVIDER_META).map(([key, meta]) => (
                            <option key={key} value={key}>{meta.label}</option>
                        ))}
                    </select>
                </div>

                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by company or user…"
                    className="br50px px1-5rem py0-5rem bw1px bss bo29 bg1 fs0-875rem fw400 c15"
                    style={{ minWidth: 260, outline: 'none' }}
                />
            </div>

            {/* Column headers */}
            <div className="df aic px1-5rem py0-5rem mb0-5rem" style={{ gap: '1.5rem' }}>
                <div style={{ width: 14, flexShrink: 0 }} />
                <span className="fs0-7rem fw600 c28 ttu" style={{ flex: '1 1 160px', letterSpacing: '0.05em' }}>Run By</span>
                <span className="fs0-7rem fw600 c28 ttu" style={{ flex: '1 1 180px', letterSpacing: '0.05em' }}>Company</span>
                <span className="fs0-7rem fw600 c28 ttu" style={{ flex: '1 1 180px', letterSpacing: '0.05em' }}>Feature</span>
                <span className="fs0-7rem fw600 c28 ttu" style={{ flex: '1 1 220px', letterSpacing: '0.05em' }}>Providers Used</span>
                <span className="fs0-7rem fw600 c28 ttu tar" style={{ flex: '0 0 100px', letterSpacing: '0.05em' }}>AI Actions</span>
                <span className="fs0-7rem fw600 c28 ttu tar" style={{ flex: '0 0 110px', letterSpacing: '0.05em' }}>Tokens</span>
                <span className="fs0-7rem fw600 c28 ttu tar" style={{ flex: '0 0 90px', letterSpacing: '0.05em' }}>Cost</span>
            </div>

            {loading ? (
                <div className="df aic jcc bg38 br10px" style={{ minHeight: '30vh' }}>
                    <p className="fs1rem fw500 c28">Loading…</p>
                </div>
            ) : logs.length ? (
                <>
                    {logs.map((run) => <RunRow key={run._id} run={run} />)}
                    <div className="df jcfe mt0-5rem">
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={limit}
                            onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[25, 50, 100]}
                        />
                    </div>
                </>
            ) : (
                <div className="df aic jcc fdc bg38 br10px" style={{ minHeight: '30vh' }}>
                    <p className="fs1rem fw500 c15 mb0-25rem">No AI usage recorded yet</p>
                    <p className="fs0-8rem fw400 c28">Costs will appear here after a Filing Assistant run completes.</p>
                </div>
            )}
        </div>
    );
};

export default AiCosting;
